// ============================================
// FAMUZA MARKET — Listings v2.0
// Cayden + Claude redesign
// No sidebar · Filter chips · Bottom drawer · 2-col grid
// ============================================

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabase';

const CATEGORIES = [
  'All',
  'Shoes',
  'Salaula',
  'Electronics',
  'Furniture — Wayfair UK',
  'Returns — ASDA/George',
  'Returns — Amazon UK',
  'Returns — Lidl',
  'Baby Products',
  'Used Toys',
  'New Toys — Amazon',
  'Accessories & Handbags',
  'Beauty & Cosmetics',
  'Home Essentials',
  'Kitchen & Appliances',
  'Fitness & Gym',
  'Power & Solar',
  'Farming & Tools',
  'Phones & Tablets',
  'Cars & Auto',
];

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest first' },
  { value: 'oldest',    label: 'Oldest first' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc',label: 'Price: High → Low' },
  { value: 'popular',   label: 'Most popular' },
];

const PRICE_PRESETS = [
  { label: 'Any price', min: 0, max: Infinity },
  { label: 'Under K500', min: 0, max: 500 },
  { label: 'K500–K2k', min: 500, max: 2000 },
  { label: 'K2k–K5k', min: 2000, max: 5000 },
  { label: 'Over K5k', min: 5000, max: Infinity },
];

function parsePrice(val) {
  return parseFloat(String(val ?? '0').replace(/[^0-9.]/g, '')) || 0;
}

function roundUpToNice(n) {
  if (n <= 0) return 10000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(n)));
  return Math.ceil(n / magnitude) * magnitude;
}

function formatK(val) {
  if (val >= 1000) return `K${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
  return `K${val}`;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr);
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

const WaIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

// ── Product Card ──────────────────────────────────────────────────────────────
function GridCard({ item, navigate, formatPrice, whatsAppEnquiry }) {
  const discount = item.old_price
    ? Math.round(((parsePrice(item.old_price) - parsePrice(item.price)) / parsePrice(item.old_price)) * 100)
    : null;
  const isNew = (Date.now() - new Date(item.created_at)) < 48 * 3600000;

  return (
    <div
      onClick={() => navigate(`/product/${item.id}`)}
      style={{ background: '#1E1E1E', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.18s, box-shadow 0.18s', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.6)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.4)'; }}
    >
      {/* Image — 80% ratio */}
      <div style={{ position: 'relative', paddingBottom: '80%', background: '#2A2A2A', overflow: 'hidden' }}>
        <img
          src={item.image_url} alt={item.title}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.35s' }}
          onError={e => e.target.style.display = 'none'}
          onMouseEnter={e => e.target.style.transform = 'scale(1.06)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}
        />
        <div style={{ position: 'absolute', top: 7, left: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {discount && (
            <span style={{ background: '#E8622A', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100, boxShadow: '0 2px 8px rgba(232,98,42,0.6)' }}>
              -{discount}%
            </span>
          )}
          {isNew && (
            <span style={{ background: '#25D366', color: '#fff', fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 100, boxShadow: '0 2px 8px rgba(37,211,102,0.5)' }}>
              🔥 NEW
            </span>
          )}
        </div>
        {(item.stock === 1 || item.quantity === 1) && (
          <div style={{ position: 'absolute', bottom: 7, left: 7, background: 'rgba(0,0,0,0.85)', color: '#ff6b6b', fontSize: '0.6rem', fontWeight: 700, padding: '3px 8px', borderRadius: 100 }}>
            🔴 Only 1 left!
          </div>
        )}
      </div>
      <div style={{ padding: '0.7rem' }}>
        {/* Price — orange, bigger */}
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#E8622A', lineHeight: 1, marginBottom: 4 }}>
          {formatPrice(item.price)}
          {item.old_price && (
            <span style={{ fontSize: '0.65rem', color: 'rgba(245,240,232,0.3)', textDecoration: 'line-through', marginLeft: 6, fontWeight: 400 }}>
              {formatPrice(item.old_price)}
            </span>
          )}
        </div>
        {/* Title — 1 line */}
        <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(245,240,232,0.9)', lineHeight: 1.3, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <div style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.35)', lineHeight: 1.5 }}>
            {item.location && <div>📍 {item.location}</div>}
            <div>🕐 {timeAgo(item.created_at)} · {item.views || 0} views</div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); whatsAppEnquiry(item, item.seller_phone); }}
            style={{ width: 34, height: 34, borderRadius: '50%', background: '#25D366', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 3px 12px rgba(37,211,102,0.45)' }}>
            <WaIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ListCard({ item, navigate, formatPrice, whatsAppEnquiry }) {
  const discount = item.old_price
    ? Math.round(((parsePrice(item.old_price) - parsePrice(item.price)) / parsePrice(item.old_price)) * 100)
    : null;
  return (
    <div
      onClick={() => navigate(`/product/${item.id}`)}
      style={{ background: '#1E1E1E', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', display: 'flex', boxShadow: '0 2px 12px rgba(0,0,0,0.4)', transition: 'box-shadow 0.18s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.6)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.4)'}
    >
      <div style={{ width: 110, flexShrink: 0, background: '#2A2A2A', position: 'relative' }}>
        <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: 110 }} onError={e => e.target.style.display = 'none'} />
        {discount && (
          <div style={{ position: 'absolute', top: 7, left: 7, background: '#E8622A', color: '#fff', fontSize: '0.6rem', fontWeight: 800, padding: '3px 7px', borderRadius: 100, boxShadow: '0 2px 6px rgba(232,98,42,0.5)' }}>
            -{discount}%
          </div>
        )}
      </div>
      <div style={{ flex: 1, padding: '0.75rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.58rem', color: '#E8622A', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase' }}>{item.category}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(245,240,232,0.9)', lineHeight: 1.35, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
          <div style={{ fontSize: '0.6rem', color: 'rgba(245,240,232,0.3)', lineHeight: 1.5 }}>
            {item.location && <span>📍 {item.location} · </span>}
            🕐 {timeAgo(item.created_at)} · {item.views || 0} views
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
          <div>
            <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: '#E8622A' }}>{formatPrice(item.price)}</span>
            {item.old_price && <span style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.3)', textDecoration: 'line-through', marginLeft: '0.4rem' }}>{formatPrice(item.old_price)}</span>}
          </div>
          <button
            onClick={e => { e.stopPropagation(); whatsAppEnquiry(item, item.seller_phone); }}
            style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 100, padding: '0.4rem 0.9rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 3px 10px rgba(37,211,102,0.35)' }}>
            <WaIcon size={13} /> Chat
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter Drawer ─────────────────────────────────────────────────────────────
function FilterDrawer({ open, onClose, selectedCategory, setSelectedCategory, sortBy, setSortBy, dealsOnly, setDealsOnly, pricePreset, setPricePreset, sliderCeiling, priceMax, setPriceMax, onApply }) {
  if (!open) return null;
  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
      {/* Drawer */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#161616', borderRadius: '20px 20px 0 0', padding: '1.25rem 1rem 2rem', zIndex: 201, maxHeight: '85vh', overflowY: 'auto' }}>
        {/* Handle */}
        <div style={{ width: 36, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, margin: '0 auto 1.25rem' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#F5F0E8' }}>Filters</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(245,240,232,0.5)', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Category */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: 100, border: 'none', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: selectedCategory === cat ? 700 : 400, background: selectedCategory === cat ? '#E8622A' : 'rgba(255,255,255,0.07)', color: selectedCategory === cat ? '#fff' : 'rgba(245,240,232,0.7)' }}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Price range */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price Range</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
            {PRICE_PRESETS.map((p, i) => (
              <button key={i} onClick={() => setPricePreset(i)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: 100, border: 'none', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: pricePreset === i ? 700 : 400, background: pricePreset === i ? '#E8622A' : 'rgba(255,255,255,0.07)', color: pricePreset === i ? '#fff' : 'rgba(245,240,232,0.7)' }}>
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.5)', marginBottom: '0.4rem' }}>
            Custom max: <strong style={{ color: '#E8622A' }}>{formatK(priceMax)}</strong>
          </div>
          <input type="range" min={0} max={sliderCeiling} step={Math.max(50, Math.floor(sliderCeiling / 100))} value={priceMax}
            onChange={e => { setPriceMax(Number(e.target.value)); setPricePreset(-1); }}
            style={{ width: '100%', accentColor: '#E8622A', cursor: 'pointer' }} />
        </div>

        {/* Sort */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort by</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {SORT_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setSortBy(o.value)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: 100, border: 'none', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: sortBy === o.value ? 700 : 400, background: sortBy === o.value ? '#E8622A' : 'rgba(255,255,255,0.07)', color: sortBy === o.value ? '#fff' : 'rgba(245,240,232,0.7)' }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Deals toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '0.75rem 1rem' }}>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#F5F0E8' }}>Deals only</div>
            <div style={{ fontSize: '0.7rem', color: 'rgba(245,240,232,0.4)' }}>Show discounted items only</div>
          </div>
          <div onClick={() => setDealsOnly(d => !d)}
            style={{ width: 44, height: 24, borderRadius: 12, background: dealsOnly ? '#E8622A' : '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px', transition: 'background 0.2s', flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'transform 0.2s', transform: dealsOnly ? 'translateX(20px)' : 'translateX(0)' }} />
          </div>
        </div>

        <button onClick={onApply}
          style={{ width: '100%', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.875rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
          Show results
        </button>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Listings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { whatsAppEnquiry, formatPrice } = useApp();

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('cat') || 'All');
  const [sortBy, setSortBy]           = useState('newest');
  const [dealsOnly, setDealsOnly]     = useState(searchParams.get('deals') === 'true');
  const [priceMax, setPriceMax]       = useState(0);
  const [sliderCeiling, setSliderCeiling] = useState(10000);
  const [pricePreset, setPricePreset] = useState(0);
  const [viewMode, setViewMode]       = useState('grid');
  const [drawerOpen, setDrawerOpen]   = useState(false);

  // Fetch
  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      const { data, error } = await supabase
        .from('products').select('*').eq('status', 'active').order('created_at', { ascending: false });
      if (!error && data) {
        setAllProducts(data);
        const ceiling = roundUpToNice(Math.max(...data.map(p => parsePrice(p.price)), 0));
        setSliderCeiling(ceiling);
        setPriceMax(ceiling);
      }
      setLoading(false);
    }
    fetchProducts();
  }, []);

  // Filter + sort
  const filtered = useMemo(() => {
    let result = [...allProducts];
    if (selectedCategory !== 'All') result = result.filter(p => p.category?.toLowerCase() === selectedCategory.toLowerCase());
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.title?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }

    // Price filter
    const preset = PRICE_PRESETS[pricePreset];
    if (preset && pricePreset > 0) {
      result = result.filter(p => {
        const price = parsePrice(p.price);
        return price >= preset.min && price <= preset.max;
      });
    } else if (pricePreset === -1) {
      result = result.filter(p => parsePrice(p.price) <= priceMax);
    }

    if (dealsOnly) result = result.filter(p => p.old_price && parsePrice(p.old_price) > parsePrice(p.price));

    switch (sortBy) {
      case 'oldest':     result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); break;
      case 'price_asc':  result.sort((a, b) => parsePrice(a.price) - parsePrice(b.price)); break;
      case 'price_desc': result.sort((a, b) => parsePrice(b.price) - parsePrice(a.price)); break;
      case 'popular':    result.sort((a, b) => (b.views || 0) - (a.views || 0)); break;
      default:           result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return result;
  }, [allProducts, selectedCategory, searchQuery, priceMax, pricePreset, dealsOnly, sortBy]);

  const activeFilterCount = [
    selectedCategory !== 'All',
    dealsOnly,
    sortBy !== 'newest',
    pricePreset > 0 || pricePreset === -1,
  ].filter(Boolean).length;

  const clearAll = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setPriceMax(sliderCeiling);
    setPricePreset(0);
    setDealsOnly(false);
    setSortBy('newest');
  };

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', color: '#F5F0E8' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Sticky filter bar ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: '#111', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '0.6rem 1rem' }}>

        {/* Search + controls row */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Link to="/" style={{ color: 'rgba(245,240,232,0.4)', fontSize: '0.75rem', textDecoration: 'none', flexShrink: 0 }}>← Home</Link>
          <div style={{ flex: 1, display: 'flex', background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 100, overflow: 'hidden' }}>
            <span style={{ padding: '0 0.6rem', display: 'flex', alignItems: 'center', color: 'rgba(245,240,232,0.4)', fontSize: '0.85rem' }}>🔍</span>
            <input
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#F5F0E8', fontSize: '0.85rem', padding: '0.55rem 0', fontFamily: 'DM Sans, sans-serif' }}
              placeholder="Search listings..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && <button onClick={() => setSearchQuery('')} style={{ background: 'transparent', border: 'none', color: 'rgba(245,240,232,0.4)', padding: '0 0.75rem', cursor: 'pointer' }}>✕</button>}
          </div>

          {/* Grid/List toggle */}
          <div style={{ display: 'flex', gap: '0.2rem', flexShrink: 0 }}>
            {['grid','list'].map(mode => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{ background: viewMode === mode ? '#E8622A' : 'rgba(255,255,255,0.07)', border: 'none', color: viewMode === mode ? '#fff' : 'rgba(245,240,232,0.5)', borderRadius: 8, padding: '0.4rem 0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                {mode === 'grid' ? '⊞' : '☰'}
              </button>
            ))}
          </div>
        </div>

        {/* Filter chips */}
        <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {/* Filter button */}
          <button onClick={() => setDrawerOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.75rem', borderRadius: 100, border: 'none', background: activeFilterCount > 0 ? '#E8622A' : 'rgba(255,255,255,0.07)', color: activeFilterCount > 0 ? '#fff' : 'rgba(245,240,232,0.7)', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0, fontFamily: 'DM Sans, sans-serif' }}>
            ⚙️ Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>

          {/* Category chips */}
          {['All', 'Deals 🔥', 'Shoes', 'Electronics', 'Furniture — Wayfair UK', 'Salaula', 'Baby Products'].map((cat, i) => {
            const isDeals = cat === 'Deals 🔥';
            const active = isDeals ? dealsOnly : (selectedCategory === cat);
            return (
              <button key={i}
                onClick={() => {
                  if (isDeals) setDealsOnly(d => !d);
                  else setSelectedCategory(cat);
                }}
                style={{ whiteSpace: 'nowrap', padding: '0.35rem 0.75rem', borderRadius: 100, border: 'none', fontSize: '0.75rem', cursor: 'pointer', flexShrink: 0, fontFamily: 'DM Sans, sans-serif', fontWeight: active ? 700 : 400, background: active ? '#E8622A' : 'rgba(255,255,255,0.07)', color: active ? '#fff' : 'rgba(245,240,232,0.7)' }}>
                {cat}
              </button>
            );
          })}

          {activeFilterCount > 0 && (
            <button onClick={clearAll}
              style={{ whiteSpace: 'nowrap', padding: '0.35rem 0.75rem', borderRadius: 100, border: '0.5px solid rgba(232,98,42,0.4)', fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0, fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: '#E8622A', fontWeight: 600 }}>
              Clear all ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Results count + list an item ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem' }}>
        <span style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.45)' }}>
          {loading ? 'Loading...' : `${filtered.length} items found`}
        </span>
        <button onClick={() => navigate('/seller/dashboard')}
          style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.45rem 1rem', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
          + List item
        </button>
      </div>

      {/* ── Products ── */}
      <div style={{ padding: '0 1rem 4rem' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {[1,2,3,4,5,6,7,8,9,10].map(i => (
              <div key={i} style={{ background: '#1E1E1E', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
                <div style={{ paddingBottom: '75%', background: 'linear-gradient(90deg,#1E1E1E 25%,#2A2A2A 50%,#1E1E1E 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                <div style={{ padding: '0.6rem' }}>
                  <div style={{ height: 7, background: '#2A2A2A', borderRadius: 4, marginBottom: 6, width: '50%' }} />
                  <div style={{ height: 9, background: '#2A2A2A', borderRadius: 4, marginBottom: 6 }} />
                  <div style={{ height: 14, background: '#2A2A2A', borderRadius: 4, width: '45%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: 'rgba(245,240,232,0.4)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🔍</div>
            <p style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>No listings match your filters.</p>
            <button onClick={clearAll}
              style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div style={viewMode === 'grid'
            ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }
            : { display: 'flex', flexDirection: 'column', gap: '0.5rem' }
          }>
            {filtered.map(item => viewMode === 'grid'
              ? <GridCard key={item.id} item={item} navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry} />
              : <ListCard key={item.id} item={item} navigate={navigate} formatPrice={formatPrice} whatsAppEnquiry={whatsAppEnquiry} />
            )}
          </div>
        )}
      </div>

      {/* ── Filter Drawer ── */}
      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
        sortBy={sortBy} setSortBy={setSortBy}
        dealsOnly={dealsOnly} setDealsOnly={setDealsOnly}
        pricePreset={pricePreset} setPricePreset={setPricePreset}
        sliderCeiling={sliderCeiling}
        priceMax={priceMax} setPriceMax={setPriceMax}
        onApply={() => setDrawerOpen(false)}
      />
    </div>
  );
}
